import { useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { screenToWorld as screenToWorldUtil, worldToGrid as worldToGridUtil, gridToWorld as gridToWorldUtil } from '../utils/CoordinateUtils';

export const useViewport = (canvasRef, gridAreaRef, containerRef, gridOffset) => {
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const [transform, setTransform] = useState({
        scale: 1,
        translateX: 0,
        translateY: 0,
        isDragging: false,
        isPanningCandidate: false,
        isMovingGrid: false,
        dragStart: { x: 0, y: 0 },
    });

    const [scrollbars, setScrollbars] = useState({
        horizontal: false,
        vertical: false,
    });

    const [scrollbarDrag, setScrollbarDrag] = useState({
        isDragging: false,
        type: null,
        startPos: 0,
        startTranslateX: 0,
        startTranslateY: 0,
    });

    const getContext = useCallback(() => {
        if (canvasRef.current) {
            return canvasRef.current.getContext('2d');
        }
        return null;
    }, [canvasRef]);

    const screenToWorld = useCallback((screenX, screenY) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        return screenToWorldUtil(screenX, screenY, rect, transform);
    }, [transform.scale, transform.translateX, transform.translateY, canvasRef]);

    const worldToGrid = useCallback((worldX, worldY) => {
        return worldToGridUtil(worldX, worldY, gridOffset);
    }, [gridOffset]);

    const gridToWorld = useCallback((gridX, gridY) => {
        return gridToWorldUtil(gridX, gridY, gridOffset);
    }, [gridOffset]);

    const redrawBackground = useCallback((ctx) => {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = 'lightblue';
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }, []);

    const clampTransform = useCallback((proposedTransform) => {
        const canvas = canvasRef.current;
        if (!canvas) return { translateX: 0, translateY: 0 };

        const { scale, translateX, translateY } = proposedTransform;
        const gridWidth = 600;
        const gridHeight = 400;

        // Calculate the boundaries for translation
        let minTranslateX = canvas.width - (gridOffset.x + gridWidth) * scale;
        let maxTranslateX = -gridOffset.x * scale;
        let minTranslateY = canvas.height - (gridOffset.y + gridHeight) * scale;
        let maxTranslateY = -gridOffset.y * scale;

        // If the scaled content is smaller than the canvas, the logic reverses.
        if (minTranslateX > maxTranslateX) {
            [minTranslateX, maxTranslateX] = [maxTranslateX, minTranslateX];
        }
        if (minTranslateY > maxTranslateY) {
            [minTranslateY, maxTranslateY] = [maxTranslateY, minTranslateY];
        }

        const clampedX = Math.max(minTranslateX, Math.min(maxTranslateX, translateX));
        const clampedY = Math.max(minTranslateY, Math.min(maxTranslateY, translateY));

        return { translateX: clampedX, translateY: clampedY };

    }, [gridOffset, canvasRef]);

    const getScrollbarProps = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return { horizontal: {}, vertical: {} };

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        const gridWidth = 600 * transform.scale;
        const gridHeight = 400 * transform.scale;

        const gridLeft = gridOffset.x * transform.scale + transform.translateX;
        const gridTop = gridOffset.y * transform.scale + transform.translateY;
        const gridRight = gridLeft + gridWidth;
        const gridBottom = gridTop + gridHeight;

        const contentLeft = Math.min(0, gridLeft);
        const contentTop = Math.min(0, gridTop);
        const contentRight = Math.max(canvasWidth, gridRight);
        const contentBottom = Math.max(canvasHeight, gridBottom);

        const totalWidth = contentRight - contentLeft;
        const totalHeight = contentBottom - contentTop;

        const visibleWidthRatio = canvasWidth / totalWidth;
        const visibleHeightRatio = canvasHeight / totalHeight;

        const horizontalThumbWidth = Math.max(20, visibleWidthRatio * canvasWidth);
        const verticalThumbHeight = Math.max(20, visibleHeightRatio * canvasHeight);

        const maxScrollX = Math.max(0, totalWidth - canvasWidth);
        const maxScrollY = Math.max(0, totalHeight - canvasHeight);

        const currentScrollX = -contentLeft;
        const currentScrollY = -contentTop;

        const thumbLeft = maxScrollX > 0 ? (currentScrollX / maxScrollX) * (canvasWidth - horizontalThumbWidth) : 0;
        const thumbTop = maxScrollY > 0 ? (currentScrollY / maxScrollY) * (canvasHeight - verticalThumbHeight) : 0;

        return {
            horizontal: {
                thumbWidth: horizontalThumbWidth,
                thumbLeft: thumbLeft,
                trackWidth: canvasWidth,
                maxScroll: maxScrollX,
            },
            vertical: {
                thumbHeight: verticalThumbHeight,
                thumbTop: thumbTop,
                trackHeight: canvasHeight,
                maxScroll: maxScrollY,
            }
        };
    }, [gridOffset, transform, dimensions, canvasRef]);

    // Handle Resizing
    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [containerRef]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        const gridLeft = gridOffset.x * transform.scale + transform.translateX;
        const gridTop = gridOffset.y * transform.scale + transform.translateY;
        const gridRight = gridLeft + (600 * transform.scale);
        const gridBottom = gridTop + (400 * transform.scale);

        const needsHorizontal = gridLeft < 0 || gridRight > canvasWidth;
        const needsVertical = gridTop < 0 || gridBottom > canvasHeight;

        setScrollbars({
            horizontal: needsHorizontal,
            vertical: needsVertical
        });
    }, [gridOffset, transform, canvasRef]);

    const handleScrollbarMouseDown = (event, type) => {
        event.preventDefault();
        event.stopPropagation();

        const rect = canvasRef.current.getBoundingClientRect();
        const startPos = type === 'horizontal' ?
            event.clientX - rect.left :
            event.clientY - rect.top;

        setScrollbarDrag({
            isDragging: true,
            type,
            startPos,
            startTranslateX: transform.translateX,
            startTranslateY: transform.translateY,
        });
    };

    const handleScrollbarMouseMove = useCallback((event) => {
        if (!scrollbarDrag.isDragging) return;
        event.preventDefault();
        event.stopPropagation();

        const rect = canvasRef.current.getBoundingClientRect();
        const currentPos = scrollbarDrag.type === 'horizontal' ?
            event.clientX - rect.left :
            event.clientY - rect.top;

        const delta = currentPos - scrollbarDrag.startPos;
        const scrollbarProps = getScrollbarProps();

        let newTranslateX = transform.translateX;
        let newTranslateY = transform.translateY;

        if (scrollbarDrag.type === 'horizontal') {
            const props = scrollbarProps.horizontal;
            const trackLength = props.trackWidth - props.thumbWidth;
            if (trackLength <= 0) return;
            const scrollRatio = delta / trackLength;
            const deltaScroll = scrollRatio * props.maxScroll;
            newTranslateX = scrollbarDrag.startTranslateX - deltaScroll;
        } else { // Vertical
            const props = scrollbarProps.vertical;
            const trackLength = props.trackHeight - props.thumbHeight;

            if (trackLength <= 0) return;
            const scrollRatio = delta / trackLength;
            const deltaScroll = scrollRatio * props.maxScroll;
            newTranslateY = scrollbarDrag.startTranslateY - deltaScroll;
        }

        // Clamp the new values before setting the state
        const { translateX: clampedX, translateY: clampedY } = clampTransform({
            ...transform,
            translateX: newTranslateX,
            translateY: newTranslateY,
        });

        setTransform(prev => ({ ...prev, translateX: clampedX, translateY: clampedY }));

    }, [scrollbarDrag, getScrollbarProps, transform, clampTransform, canvasRef]);

    const handleScrollbarMouseUp = useCallback(() => {
        setScrollbarDrag({
            isDragging: false,
            type: null,
            startPos: 0,
            startTranslateX: 0,
            startTranslateY: 0,
        });
    }, []);

    useEffect(() => {
        if (scrollbarDrag.isDragging) {
            document.addEventListener('mousemove', handleScrollbarMouseMove);
            document.addEventListener('mouseup', handleScrollbarMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleScrollbarMouseMove);
                document.removeEventListener('mouseup', handleScrollbarMouseUp);
            };
        }
    }, [scrollbarDrag.isDragging, handleScrollbarMouseMove, handleScrollbarMouseUp]);

    useEffect(() => {
        const ctx = getContext();
        if (ctx) {
            redrawBackground(ctx);
        }
    }, [redrawBackground, dimensions, getContext]);

    const handleWheel = (event) => {
        const scaleFactor = 1.1;
        const delta = event.deltaY < 0 ? scaleFactor : 1 / scaleFactor;
        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        setTransform(prev => {
            const newScale = Math.max(0.1, Math.min(50, prev.scale * delta));
            const newTranslateX = mouseX - ((mouseX - prev.translateX) * (newScale / prev.scale));
            const newTranslateY = mouseY - ((mouseY - prev.translateY) * (newScale / prev.scale));

            const { translateX: clampedX, translateY: clampedY } = clampTransform({
                scale: newScale,
                translateX: newTranslateX,
                translateY: newTranslateY,
            });

            return {
                ...prev,
                scale: newScale,
                translateX: clampedX,
                translateY: clampedY,
            };
        });
    };

    const handleZoomIn = () => {
        const scaleFactor = 1.1;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        setTransform(prev => {
            const newScale = Math.min(50, prev.scale * scaleFactor);
            const newTranslateX = centerX - ((centerX - prev.translateX) * (newScale / prev.scale));
            const newTranslateY = centerY - ((centerY - prev.translateY) * (newScale / prev.scale));
            return { ...prev, scale: newScale, translateX: newTranslateX, translateY: newTranslateY };
        });
    };

    const handleZoomOut = () => {
        const scaleFactor = 1.1;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        setTransform(prev => {
            const newScale = Math.max(0.1, prev.scale / scaleFactor);
            const newTranslateX = centerX - ((centerX - prev.translateX) * (newScale / prev.scale));
            const newTranslateY = centerY - ((centerY - prev.translateY) * (newScale / prev.scale));
            return { ...prev, scale: newScale, translateX: newTranslateX, translateY: newTranslateY };
        });
    };

    const handleResetZoom = () => {
        setTransform(prev => ({ ...prev, scale: 1, translateX: 0, translateY: 0 }));
    };

    const handleFit = () => {
        const canvas = canvasRef.current;
        const gridArea = gridAreaRef.current;
        if (!canvas || !gridArea) return;

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        const objects = gridArea.getGraphicObjects();
        const gridOffset = gridArea.getGridOffset();

        if (objects && objects.length > 0) {
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            let hasObjects = false;

            objects.forEach(obj => {
                if (obj.getBounds) {
                    const bounds = obj.getBounds();
                    if (bounds) {
                        hasObjects = true;
                        minX = Math.min(minX, bounds.minX + gridOffset.x);
                        maxX = Math.max(maxX, bounds.maxX + gridOffset.x);
                        minY = Math.min(minY, bounds.minY + gridOffset.y);
                        maxY = Math.max(maxY, bounds.maxY + gridOffset.y);
                    }
                }
            });

            if (hasObjects) {
                const padding = 50;
                const contentWidth = maxX - minX + padding * 2;
                const contentHeight = maxY - minY + padding * 2;

                const scaleX = canvasWidth / contentWidth;
                const scaleY = canvasHeight / contentHeight;
                const newScale = Math.min(scaleX, scaleY, 50);

                const newTranslateX = (canvasWidth - (maxX + minX) * newScale) / 2;
                const newTranslateY = (canvasHeight - (maxY + minY) * newScale) / 2;

                setTransform(prev => ({
                    ...prev,
                    scale: newScale,
                    translateX: newTranslateX,
                    translateY: newTranslateY
                }));
            }
        }
    };

    const handleFitCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const gridWidth = 600;
        const gridHeight = 400;

        const scaleX = canvas.width / gridWidth;
        const scaleY = canvas.height / gridHeight;
        const newScale = Math.min(scaleX, scaleY, 50);

        const newTranslateX = (canvas.width - gridWidth * newScale) / 2;
        const newTranslateY = (canvas.height - gridHeight * newScale) / 2;

        setTransform(prev => ({
            ...prev,
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY
        }));
    };

    return {
        dimensions,
        transform,
        setTransform,
        scrollbars,
        scrollbarDrag,
        screenToWorld,
        worldToGrid,
        gridToWorld,
        handleWheel,
        handleZoomIn,
        handleZoomOut,
        handleResetZoom,
        handleFit,
        handleFitCanvas,
        handleScrollbarMouseDown,
        getScrollbarProps,
        getContext,
        redrawBackground,
        clampTransform
    };
};
